import type { FeatureSection } from './types'

export const programmeTrackersSection: FeatureSection = {
  key: 'programme-trackers',
  title: 'Programme trackers',
  routes: ['programme-tracker', 'admin-programme-trackers', 'admin-programme-tracker-edit'],
  audience: 'everyone',
  about:
    'A programme tracker is a read-only page that shows how a programme of work is going: its sections, the items in each, where each item is on its steps, and the updates posted about it. The customer’s people read it at /projects/<slug>. Realm admins and Managers build and update it in Admin → Programme trackers. The page shows only what someone has posted there.',
  image: '/help/screenshots/programme-trackers/_section.png',
  imageAlt: 'A demo programme tracker: the branded header with Last updated, the title, a summary ring for each section and Overall, and the Reference tab open.',
  features: [
    {
      key: 'tracker-page',
      name: 'Programme tracker',
      what: 'The tracker itself: a branded header showing when it was last updated, a progress summary, a Reference tab and one tab per section.',
      how: 'Open the tracker’s link; Managers also find it as Programme tracker in the Admin menu. Click a tab to read that section.',
      controls: ['Reference'],
      agentNotes:
        'Only people in the tracker’s customer, and Managers, can open it; anyone else gets an error. A disabled tracker does not open. Changes show on the next load of the page.',
      image: '/help/screenshots/programme-trackers/tracker-page.png',
      imageAlt: 'The top of a programme tracker: the customer’s name, the subtitle, Last updated, the summary rings and the tabs.',
    },
    {
      key: 'summary',
      name: 'Summary',
      what: 'One ring per section showing how many items are done out of the total, and an Overall ring with the programme’s percentage.',
      how: 'Read it under the header; it updates as items reach their section’s done status.',
      controls: ['Overall'],
      agentNotes:
        'An item counts as done when its status equals its section’s done status exactly. A section with no done status never counts an item as done. Overall is done items over all items, across every section.',
      image: '/help/screenshots/programme-trackers/summary.png',
      imageAlt: 'The summary: a ring per section with its done count and the Overall percentage.',
    },
    {
      key: 'reference',
      name: 'Reference',
      what: 'The tracker’s background: The Vision, a Development Summary of the sections, the Leadership structure, and Programme updates, the newest updates from every item.',
      how: 'Open the Reference tab, the first tab.',
      controls: ['The Vision', 'Development Summary', 'Leadership structure', 'Programme updates'],
      agentNotes: 'Programme updates lists at most 60 entries, newest first.',
      image: '/help/screenshots/programme-trackers/reference.png',
      imageAlt: 'The Reference tab: The Vision, then the Development Summary with one card per section.',
    },
    {
      key: 'section-view',
      name: 'Section',
      what: 'One section’s items as cards. Each card shows the item’s number, title, job ID, objective, status, a progress rail of the section’s steps, its point of contact and its latest update. Details open the description, acceptance criteria, module lead, UAT and accepted dates, and notes.',
      how: 'Open a section’s tab and open an item’s Details. Details shows only when the item has a description, acceptance criteria, notes or a date.',
      controls: ['Details'],
      agentNotes:
        'Steps before the item’s current step are complete, its current step is active, and later steps are pending. A done item shows every step complete, with the last one marked Live. An item’s latest note is its newest update by date.',
      image: '/help/screenshots/programme-trackers/section-view.png',
      imageAlt: 'A section tab: its steps, then item cards with a progress rail, a status chip, the latest update and an open Details panel.',
    },
    {
      key: 'manage-trackers',
      name: 'Programme trackers',
      audience: 'realm',
      what: 'The admin list of the customer’s trackers, with each one’s slug, section count and whether it is enabled.',
      how: 'Open Admin, then Manage trackers under Programme trackers. Use the row icons to edit content, view the tracker, edit its details or delete it.',
      controls: ['Manage trackers', 'New tracker', 'Edit content', 'View tracker', 'Edit details', 'Delete', 'Enabled', 'Disabled', 'Finance view only'],
      agentNotes:
        'Realm admins and Managers can use this page. Deleting a tracker removes every section, item and update in it and cannot be undone.',
    },
    {
      key: 'tracker-details',
      name: 'New tracker and Edit tracker',
      audience: 'realm',
      what: 'A tracker’s address, title and branding, and who can see it.',
      how: 'Click New tracker, or Edit details on a row. Fill in the fields and click Save.',
      controls: ['Slug', 'Customer', 'Title', 'Subtitle', 'Customer name', 'Logo URL', 'Primary colour', 'Accent colour', 'Enabled', 'Finance view only', 'Save', 'Cancel'],
      settings: [
        'Slug: the tracker’s address, /projects/<slug>. Lower-case letters, digits and hyphens; it must be unique.',
        'Enabled: off hides the tracker; its page does not open.',
        'Finance view only: on shows the tracker only to Managers and to people a Manager has given the Finance view. Off by default.',
      ],
      agentNotes:
        'With Finance view only on, anyone else is refused the page with “This programme tracker is only shown to people with the Finance view. Ask a Bayes Price Manager.” and the tracker drops out of their Admin list and menu. A Realm admin without the Finance view cannot open or change such a tracker.',
    },
    {
      key: 'headline',
      name: 'Headline',
      audience: 'realm',
      what: 'The tracker’s title, subtitle, vision and leadership rows, shown on the Reference tab.',
      how: 'Click Edit content on a tracker. Change the fields, add or remove leadership rows, then click Save headline.',
      controls: ['Title', 'Subtitle', 'Vision', 'Leadership rows', 'Stream', 'Role', 'Bayes Price', 'Client', 'Add leadership row', 'Save headline', 'View tracker', 'Back to Programme trackers'],
    },
    {
      key: 'sections',
      name: 'Sections',
      audience: 'realm',
      what: 'The tracker’s tabs. Each section has a name, a description, a Bayes Price lead and a client lead, up to 12 steps, and a done status.',
      how: 'Click Add section, or the pencil on a section. Fill in the fields, add steps with Add step, order them with the arrows, then save. The arrows on a section move it up or down.',
      controls: ['Add section', 'Edit section', 'Delete section', 'Name', 'Description', 'BP lead', 'Client lead', 'Done status', 'Add step'],
      settings: ['Done status: the item status that counts as done in this section.'],
      agentNotes: 'Deleting a section removes every item and update inside it and cannot be undone.',
    },
    {
      key: 'items',
      name: 'Items',
      audience: 'realm',
      what: 'The tracked pieces of work in a section. An item has a title, number, job ID, point of contact, Bayes Price lead, status, current step, objective, scope, acceptance criteria, notes, and UAT and accepted dates.',
      how: 'Click Add item in a section, or the pencil on an item. Fill in the fields and save. The arrows move an item up or down; Section moves an existing item to another section.',
      controls: ['Add item', 'Edit item', 'Delete item', 'Title', 'Section', 'Number', 'Job ID', 'POC', 'BP lead', 'Status', 'Current step', 'Objective', 'Scope', 'Acceptance criteria', 'Body', 'UAT date', 'Accepted date'],
      agentNotes:
        'Current step runs from not started up to the section’s last step. Scope shows on the tracker as the description, and Body as notes. Deleting an item removes its updates.',
    },
    {
      key: 'updates',
      name: 'Updates',
      audience: 'realm',
      what: 'The status lines posted on an item. The newest one shows on the item’s card, and all of them feed Programme updates.',
      how: 'Click the updates icon on an item, then Add update. Write the text, check the author and date, and click Post. Each posted update can be edited or deleted.',
      controls: ['Updates', 'Add update', 'Text', 'Author', 'Date', 'Post', 'Save', 'Close'],
      agentNotes:
        'Author is free text and is the name readers see. It starts as your name, or your email when your account has no name; change it when posting for someone else. A blank Author is saved as your email. Date starts as today. Apart from what Author holds, the poster’s email is never shown on the tracker. Deleting an update asks for no confirmation.',
    },
  ],
  steps: [
    'Open Admin → Manage trackers and click New tracker; set the slug, title and branding.',
    'Click Edit content and fill in the headline.',
    'Add sections with their steps and done status, then add items to each.',
    'Post updates on items as work moves on.',
    'Click View tracker to see the page the customer reads.',
  ],
}
